import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BookOpen } from 'lucide-react';

interface PodcastStorySectionProps {
  placeName: string;
  script: string;
  language: 'FR' | 'EN';
}

export const PodcastStorySection: React.FC<PodcastStorySectionProps> = ({ 
  placeName, 
  script, 
  language 
}) => {
  // Don't render if no script
  if (!script || script.trim().length === 0) {
    return null;
  }

  const title = language === 'FR' 
    ? `Histoire de ${placeName}`
    : `Story of ${placeName}`;

  // Format the script text into paragraphs for better readability
  const formatScript = (text: string) => {
    // Split by double line breaks or period followed by capital letter
    const paragraphs = text
      .split(/\n\n+/)
      .map(p => p.trim())
      .filter(p => p.length > 0);

    // If no double line breaks found, try to split by sentences
    if (paragraphs.length === 1) {
      return <p className="text-muted-foreground leading-relaxed text-sm whitespace-pre-line">
        {text}
      </p>;
    }

    return paragraphs.map((paragraph, index) => (
      <p key={index} className="text-muted-foreground leading-relaxed text-sm">
        {paragraph}
      </p>
    ));
  };

  return (
    <article>
      <Card className="overflow-hidden bg-gradient-to-br from-amber-50/30 via-orange-50/20 to-amber-50/30 dark:from-amber-950/10 dark:via-orange-950/10 dark:to-amber-950/10 border-amber-200/50 dark:border-amber-800/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-500" />
            <h2>{title}</h2>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {formatScript(script)}
          </div>
        </CardContent>
      </Card>
    </article>
  );
};
